import {
    useId,
    useMemo,
    useState,
    type ReactNode,
} from 'react'

import {
    useTranslation,
} from 'react-i18next'

import {
    FileTrigger,
} from 'react-aria-components'

import {
    FileText,
    Upload,
    X,
} from 'lucide-react'

import { Button } from './button'
import { cn } from '@/shared/lib/cn'

function fileKey(file: File): string {
    return [
        file.name,
        file.size,
        file.lastModified,
    ].join('-')
}

function formatFileSize(
    bytes: number,
    numberFormatter: Intl.NumberFormat,
): string {
    if (bytes < 1024) {
        return `${numberFormatter.format(bytes)} B`
    }

    if (bytes < 1024 * 1024) {
        return `${numberFormatter.format(
            bytes / 1024,
        )} KB`
    }

    return `${numberFormatter.format(
        bytes / (1024 * 1024),
    )} MB`
}

export interface FileInputProps {
    label: ReactNode

    description?: ReactNode
    errorMessage?: ReactNode

    acceptedFileTypes?: readonly string[]
    allowsMultiple?: boolean

    files?: readonly File[]
    defaultFiles?: readonly File[]

    onFilesChange?: (
        files: readonly File[],
    ) => void

    isDisabled?: boolean
    isRequired?: boolean
    isInvalid?: boolean

    /**
     * Optional overrides for translated defaults.
     */
    browseLabel?: string
    emptyLabel?: string

    getSelectedFilesLabel?: (
        count: number,
    ) => string

    getRemoveFileLabel?: (
        file: File,
    ) => string

    className?: string
}

export function FileInput({
    label,
    description,
    errorMessage,
    acceptedFileTypes,
    allowsMultiple = false,
    files,
    defaultFiles = [],
    onFilesChange,
    isDisabled = false,
    isRequired = false,
    isInvalid = Boolean(errorMessage),
    browseLabel,
    emptyLabel,
    getSelectedFilesLabel,
    getRemoveFileLabel,
    className,
}: FileInputProps) {
    const {
        t,
        i18n,
    } = useTranslation()

    const descriptionId = useId()
    const errorId = useId()

    const [
        internalFiles,
        setInternalFiles,
    ] = useState<readonly File[]>(
        defaultFiles,
    )

    const selectedFiles =
        files ?? internalFiles

    const currentLanguage =
        i18n.resolvedLanguage ??
        i18n.language

    const numberFormatter =
        useMemo(
            () =>
                new Intl.NumberFormat(
                    currentLanguage,
                    {
                        maximumFractionDigits: 1,
                    },
                ),
            [currentLanguage],
        )

    const resolvedBrowseLabel =
        browseLabel ??
        t('ui.file.browse')

    const resolvedEmptyLabel =
        emptyLabel ??
        t('ui.file.noneSelected')

    const describedBy = [
        description
            ? descriptionId
            : undefined,
        errorMessage
            ? errorId
            : undefined,
    ]
        .filter(Boolean)
        .join(' ')

    function resolveSelectedFilesLabel(
        count: number,
    ): string {
        if (getSelectedFilesLabel) {
            return getSelectedFilesLabel(
                count,
            )
        }

        /*
         * Using explicit keys keeps this fully
         * type-safe with the English translation shape.
         */
        return count === 1
            ? t('ui.file.selected_one', {
                count,
            })
            : t('ui.file.selected_other', {
                count,
            })
    }

    function resolveRemoveFileLabel(
        file: File,
    ): string {
        return (
            getRemoveFileLabel?.(file) ??
            t('ui.file.remove', {
                fileName: file.name,
            })
        )
    }

    function updateFiles(
        nextFiles: readonly File[],
    ): void {
        if (files === undefined) {
            setInternalFiles(nextFiles)
        }

        onFilesChange?.(nextFiles)
    }

    function handleSelection(
        fileList: FileList | null,
    ): void {
        const nextFiles = fileList
            ? Array.from(fileList)
            : []

        updateFiles(nextFiles)
    }

    function removeFile(
        fileToRemove: File,
    ): void {
        updateFiles(
            selectedFiles.filter(
                (file) =>
                    fileKey(file) !==
                    fileKey(fileToRemove),
            ),
        )
    }

    return (
        <div
            className={cn(
                'flex w-full flex-col gap-1.5',
                className,
            )}
        >
            <div className="flex items-center gap-1 text-sm font-medium">
                {label}

                {isRequired ? (
                    <span
                        aria-hidden="true"
                        className="text-(--color-danger)"
                    >
                        *
                    </span>
                ) : null}
            </div>

            <div
                className={cn(
                    [
                        'rounded-lg border',
                        'bg-(--color-surface)',
                        'p-4',
                    ],
                    isInvalid
                        ? 'border-(--color-danger)'
                        : 'border-(--color-border)',
                    isDisabled &&
                    'opacity-60',
                )}
            >
                <div className="flex flex-wrap items-center gap-3">
                    <FileTrigger
                        acceptedFileTypes={
                            acceptedFileTypes
                        }
                        allowsMultiple={allowsMultiple}
                        onSelect={handleSelection}
                    >
                        <Button
                            type="button"
                            variant="secondary"
                            isDisabled={isDisabled}
                            aria-invalid={
                                isInvalid
                                    ? true
                                    : undefined
                            }
                            aria-describedby={
                                describedBy ||
                                undefined
                            }
                        >
                            <Upload
                                aria-hidden="true"
                                className="h-4 w-4"
                            />

                            {resolvedBrowseLabel}
                        </Button>
                    </FileTrigger>

                    <span className="text-sm text-(--color-muted-foreground)">
                        {selectedFiles.length === 0
                            ? resolvedEmptyLabel
                            : resolveSelectedFilesLabel(
                                selectedFiles.length,
                            )}
                    </span>
                </div>

                {selectedFiles.length > 0 ? (
                    <ul className="mt-4 space-y-2">
                        {selectedFiles.map(
                            (file) => (
                                <li
                                    key={fileKey(file)}
                                    className="flex items-center gap-3 rounded-md bg-(--color-surface-muted) px-3 py-2"
                                >
                                    <FileText
                                        aria-hidden="true"
                                        className="h-4 w-4 shrink-0 text-(--color-muted-foreground)"
                                    />

                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-sm font-medium">
                                            {file.name}
                                        </div>

                                        <div className="text-xs text-(--color-muted-foreground)">
                                            {formatFileSize(
                                                file.size,
                                                numberFormatter,
                                            )}
                                        </div>
                                    </div>

                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="small"
                                        className="h-8 w-8 p-0"
                                        aria-label={resolveRemoveFileLabel(
                                            file,
                                        )}
                                        isDisabled={
                                            isDisabled
                                        }
                                        onPress={() => {
                                            removeFile(
                                                file,
                                            )
                                        }}
                                    >
                                        <X
                                            aria-hidden="true"
                                            className="h-4 w-4"
                                        />
                                    </Button>
                                </li>
                            ),
                        )}
                    </ul>
                ) : null}
            </div>

            {description ? (
                <p
                    id={descriptionId}
                    className="text-xs leading-5 text-(--color-muted-foreground)"
                >
                    {description}
                </p>
            ) : null}

            {errorMessage ? (
                <p
                    id={errorId}
                    role="alert"
                    className="text-xs leading-5 text-(--color-danger)"
                >
                    {errorMessage}
                </p>
            ) : null}
        </div>
    )
}